import { Helmet } from "react-helmet-async";
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Stack,
  Container,
  Dialog,
  Button,
  Skeleton,
} from "@mui/material";

import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LinkIcon from "@mui/icons-material/Link";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import ShareArticle from "../share-article/ShareArticle";
import { NewsStore } from "../../store/newsStore";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL } from "../../api/api";
import Preloader from "../preloader/PreLoader";
import dayjs from "dayjs";

const noImage = "https://upload.wikimedia.org/wikipedia/commons/d/d1/Image_not_available.png";

const ImageWithLoader = ({ src, alt, onClick, isSingle }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [src]);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      if (imgRef.current.naturalWidth === 0) setError(true);
      else setLoaded(true);
    }
  }, [src]);

  if (error) return null;

  return (
    <Box sx={{ width: "100%", mt: isSingle ? 3 : 0, position: "relative", borderRadius: 3, overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
      {!loaded && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={400}
          animation="wave"
          sx={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}
        />
      )}

      <Box
        component="img"
        ref={imgRef}
        src={src}
        alt={alt}
        onClick={onClick}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        sx={{
          width: "100%",
          height: loaded ? "auto" : { xs: 250, sm: 350, md: 450 },
          maxHeight: { xs: 350, sm: 480, md: 600, lg: 700 },
          objectFit: "cover",
          objectPosition: "center center",
          cursor: "pointer",
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.4s ease-in-out",
        }}
      />
    </Box>
  );
};

export default function NewsDetails() {
  const { fetchNewsDetails, newsDetails } = NewsStore();
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [zoomLevel, setZoomLevel] = useState(1);

  const handleImageClick = (imgSrc) => {
    setSelectedImage(imgSrc);
    setZoomLevel(1);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setSelectedImage("");
    setZoomLevel(1);
  };

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.5, 4));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.5, 0.5));

  useEffect(() => {
    const loadData = async () => {
      if (!slug) {
        setPageError("News slug missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setPageError("");
      try {
        await fetchNewsDetails(slug);
      } catch (error) {
        console.error(error);
        setPageError("Failed to load news details.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug, fetchNewsDetails]);

  if (loading) return <Preloader />;

  const news = newsDetails?.data ?? newsDetails ?? {};

  if (pageError) {
    return (
      <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", mt: 16 }}>
        <Typography color="error">{pageError}</Typography>
      </Box>
    );
  }

  if (!news || Object.keys(news).length === 0) {
    return (
      <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", mt: 16 }}>
        <Typography>
          Loading news details...
        </Typography>
      </Box>
    );
  }

  const getImageUrl = (path) => {
    if (!path) return "";
    return path.startsWith("http") ? path : `${BASE_URL}/${path}`;
  };

  let articleImages = [];
  if (news?.slider_images?.length > 0) {
    articleImages = news.slider_images.map(getImageUrl);
  } else if (news?.all_images?.length > 0) {
    articleImages = news.all_images.map(getImageUrl);
  } else if (news?.image) {
    articleImages = [getImageUrl(news.image)];
  }

  const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  // मेटा डिस्क्रिप्शन के लिए HTML टैग्स साफ करने का फंक्शन
  const cleanDescription = (htmlMarkup) => {
    if (!htmlMarkup) return "";
    const decoded = decodeHtml(htmlMarkup);
    return decoded.replace(/<[^>]*>/g, "").substring(0, 150);
  };

  const SITE_URL = process.env.REACT_APP_API_BASE_URL || "";

  // Ye extra slash ko end se hata dega
  const cleanSiteUrl = SITE_URL.replace(/\/+$/, "");

  const shareUrl = `${cleanSiteUrl}/${news.slug}`;

  const previewImage = articleImages.length > 0 ? articleImages[0] : noImage;

  const copyToClipboard = (text) => {
    // Use modern clipboard API if available (secure contexts)
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => alert("Link copied!"))
        .catch(err => console.error('Failed to copy: ', err));
    } else {
      // Fallback for insecure contexts (HTTP) or older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;

      // Make the textarea non-editable and invisible
      textArea.style.position = "absolute";
      textArea.style.left = "-9999px";
      textArea.setAttribute("readonly", "");

      document.body.appendChild(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
        alert("Link copied!");
      } catch (err) {
        console.error('Fallback: Oops, unable to copy', err);
      }

      document.body.removeChild(textArea);
    }
  };

  const metaDescription = cleanDescription(news?.description);

  const renderVideo = (videoLink) => {
    if (!videoLink) return null;

    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = videoLink.match(youtubeRegex);

    if (match && match[1]) {
      return (
        <Box sx={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: 3, boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
          <iframe
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
            src={`https://www.youtube.com/embed/${match[1]}`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%", borderRadius: 3, overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
        <video controls preload="metadata" style={{ width: "100%", height: "auto", display: "block" }} src={getImageUrl(videoLink)}>
          Your browser does not support the video tag.
        </video>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", color: "text.primary", mt: { xs: 20, sm: 22, md: 24 } }}>

      {/* Dynamic SEO Meta Tags for Social Sharing */}
      <Helmet>
        <title>{news?.title || "Janpath News 24"}</title>
        <meta name="description" content={metaDescription} />

        {/* Open Graph / Facebook / WhatsApp */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={news?.title || ""} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={previewImage} />
        <meta property="og:image:secure_url" content={previewImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={shareUrl} />
        <meta name="twitter:title" content={news?.title || ""} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={previewImage} />
      </Helmet>

      <Container maxWidth="lg">
        <Box sx={{ py: { xs: 3, md: 5 } }}>
          <Box component="section" sx={{ maxWidth: "800px", mx: "auto" }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              <Avatar
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (news.author?.id) navigate(`/author/${news.author.id}`);
                }}
                src={
                  news.author?.profile_image
                    ? news.author.profile_image.startsWith("http")
                      ? news.author.profile_image
                      : `${BASE_URL.replace(/\/$/, "")}/${news.author.profile_image.replace(/^\//, "")}`
                    : ""
                }
                sx={{ width: 48, height: 48 }}
              />
              <Box>
                <Typography
                  fontWeight={600}
                  sx={{
                    fontSize: "16px",
                    transition: "0.3s",
                    cursor: "pointer",
                    "&:hover": { color: "#ff184e" },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (news.author?.id) navigate(`/author/${news.author.id}`);
                  }}
                >
                  {news.author?.name}
                </Typography>
                <Typography color="text.secondary" fontSize="13px">
                  {dayjs(news?.published_at).format("MMMM D, YYYY h:mm A")}
                </Typography>
              </Box>
            </Stack>

            <Typography
              variant="h1"
              fontWeight={800}
              sx={{ fontSize: { xs: "28px", sm: "36px", md: "44px" }, lineHeight: 1.2, my: 3 }}
            >
              {news?.title || "-"}
            </Typography>

            <Stack direction="row" spacing={1.5} mt={2} mb={4} alignItems="center">
              <Typography variant="body2" fontWeight={600} color="text.secondary" mr={1}>
                Share:
              </Typography>
              <IconButton
                onClick={() => window.open(
                  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
                  "_blank"
                )}
                sx={{ color: "#1877F2", bgcolor: "action.hover", borderRadius: "10px" }}
                size="small"
              >
                <FacebookIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  const shareText = `${news?.title}\n\n${shareUrl}`;
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, "_blank");
                }}
                sx={{ color: "#1DA1F2", bgcolor: "action.hover", borderRadius: "10px" }}
                size="small"
              >
                <TwitterIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  const shareText = `${news?.title}\n\n${shareUrl}`;
                  window.open(
                    `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`,
                    "_blank"
                  );
                }}
                sx={{ color: "#25D366", bgcolor: "action.hover", borderRadius: "10px" }}
                size="small"
              >
                <WhatsAppIcon />
              </IconButton>
              <IconButton
                onClick={() => copyToClipboard(shareUrl)}
                sx={{ color: "#757575", bgcolor: "action.hover", borderRadius: "10px" }}
                size="small"
              >
                <LinkIcon />
              </IconButton>
            </Stack>

            {articleImages.length > 1 ? (
              <Swiper
                modules={[Navigation, Pagination]}
                navigation
                pagination={{ clickable: true }}
                spaceBetween={10}
                slidesPerView={1}
                style={{ borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}
              >
                {articleImages.map((img, index) => (
                  <SwiperSlide key={index}>
                    <ImageWithLoader
                      src={img}
                      alt={`Slide ${index + 1}`}
                      onClick={() => handleImageClick(img)}
                      isSingle={false}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              articleImages.length === 1 && (
                <ImageWithLoader
                  src={articleImages[0]}
                  alt={news?.title || "News Image"}
                  onClick={() => handleImageClick(articleImages[0])}
                  isSingle={true}
                />
              )
            )}

            <Dialog
              open={previewOpen}
              onClose={handlePreviewClose}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                style: {
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                },
              }}
            >
              <Box sx={{ position: "relative", textAlign: "center", width: "100%", height: "85vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1, zIndex: 10 }}>
                  <IconButton onClick={handleZoomIn} sx={{ color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}>
                    <ZoomInIcon />
                  </IconButton>
                  <IconButton onClick={handleZoomOut} sx={{ color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}>
                    <ZoomOutIcon />
                  </IconButton>
                  <IconButton onClick={handlePreviewClose} sx={{ color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box sx={{ width: "100%", height: "100%", overflow: "auto", display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <img
                    src={selectedImage}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: "8px",
                      transform: `scale(${zoomLevel})`,
                      transition: "transform 0.3s ease",
                      transformOrigin: "center center",
                    }}
                  />
                </Box>
              </Box>
            </Dialog>

            {news?.video_link && (
              <Box mt={5}>
                <Typography variant="h5" fontWeight={700} mb={2}>
                  Watch Video
                </Typography>
                {renderVideo(news.video_link)}
              </Box>
            )}

            <Typography
              sx={{ mt: 4, color: "text.primary", fontSize: { xs: "16px", md: "18px" }, lineHeight: 1.8, "& p": { mb: 2 } }}
              dangerouslySetInnerHTML={{ __html: decodeHtml(news?.description || "") }}
            />

            {news?.tags && (
              <Box sx={{ mt: 4, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                  Tags:
                </Typography>
                {(typeof news.tags === "string"
                  ? news.tags.split(",")
                  : Array.isArray(news.tags)
                    ? news.tags
                    : []
                ).map((tag, index) => {
                  const tagName = typeof tag === "object" ? tag?.name : tag;
                  const tagSlug =
                    typeof tag === "object"
                      ? tag?.slug || tag?.name
                      : String(tag).trim();
                  return (
                    tagName && (
                      <Button
                        key={index}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (tagSlug) {
                            navigate(`/chhattisgarh?tag=${tagSlug}`);
                          }
                        }}
                        sx={{
                          textTransform: "none",
                          borderRadius: 5,
                          color: "text.secondary",
                          borderColor: "divider",
                          fontSize: "13px",
                          "&:hover": {
                            borderColor: "#ff184e",
                            color: "#ff184e",
                            bgcolor: "transparent",
                          },
                        }}
                      >
                        {String(tagName).trim()}
                      </Button>
                    )
                  );
                })}
              </Box>
            )}

            <Box mt={5}>
              <ShareArticle author={news.author} />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
